import styled, { css } from 'styled-components/macro'
import Text from 'components/shared/Text'
import { respondAbove } from 'styles/mediaQueries'
import { resolveResponsiveProp } from 'styles/mixins'
import { ButtonVariant } from 'styles/types'
import { Wrapper as Icon } from 'components/shared/Icon/styles'
import { rotateSequence } from 'styles/animations'

export type Width = 'initial' | '100%' | '180px'
interface WrapperProps {
    variant: ButtonVariant
    isIconOnly: boolean
    hasArrow: boolean
    width?: Width | [Width, Width]
    isLoading?: boolean
}

export const ButtonLoaderWrapper = styled.img`
    animation: ${rotateSequence} 1s linear infinite;
`

export const Wrapper = styled.button<WrapperProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    border: 2px solid transparent;
    border-radius: 6px;
    transition: background-color 100ms ease-in, border-color 100ms ease-in;
    background-color: ${({ theme, variant }) => theme.button[variant].bgColor};
    border-color: ${({ theme, variant }) => theme.button[variant].borderColor};
    height: 44px;

    ${({ theme, variant }) => {
        const hasDarkMode = theme?.mode === 'dark'
        const hasBorderPrimaryVariant = variant === 'borderPrimary'

        if (hasDarkMode && hasBorderPrimaryVariant)
            return css`
                // static color is only possible way here without adding massive logic
                border-color: #fff !important;
                ${Text} {
                    color: #fff !important;
                }
            `
    }}

    @media ${respondAbove.xl} {
        border-radius: 8px;
        height: 48px;
    }

    ${({ isIconOnly, width, hasArrow }) =>
        !isIconOnly
            ? css`
                  padding: 12px ${hasArrow ? '14px' : '28px'};
                  width: 100%;

                  @media ${respondAbove.xl} {
                      padding: 12px ${hasArrow ? '14px' : '32px'};
                      min-width: ${width ? 'auto' : '270px'};
                      width: auto;
                  }
              `
            : css`
                  width: 44px;

                  @media ${respondAbove.xl} {
                      width: 48px;
                  }
              `};

    ${({ width }) =>
        width &&
        resolveResponsiveProp({
            type: 'width',
            value: width,
        })}

    ${Text} {
        ${({ isIconOnly, hasArrow }) =>
            !isIconOnly &&
            hasArrow &&
            css`
                transform: translateX(14px);

                @media ${respondAbove.xl} {
                    transform: translateX(16px);
                }
            `}

        ${({ isLoading, hasArrow, isIconOnly }) =>
            !isIconOnly &&
            isLoading &&
            css`
                transform: translateX(${hasArrow ? '28px' : '14px'});

                @media ${respondAbove.xl} {
                    transform: translateX(${hasArrow ? '32px' : '16px'});
                }
            `}

        position: relative;
        color: ${({ theme, variant }) => theme.button[variant].contentColor};
        text-align: center;
        display: flex;
        transition: transform ease-in-out 100ms;

        & + [class*=' icon-'] {
            color: ${({ theme, variant }) =>
                theme.button[variant].contentColor};
            opacity: 0;
            margin-left: 8px;
            transition: margin ease-in-out 200ms, opacity ease-in-out 100ms;
        }

        ${ButtonLoaderWrapper} {
            position: absolute;
            left: -32px;
        }
    }

    &:focus-visible:not(:disabled) {
        ${({ isIconOnly, hasArrow }) =>
            !isIconOnly &&
            hasArrow &&
            css`
                ${Text} {
                    transform: translateX(0px);
                }
            `}

        &:focus-visible:not(:disabled) {
            ${({ isIconOnly, hasArrow }) =>
                !isIconOnly &&
                hasArrow &&
                css`
                    ${Text} {
                        transform: translateX(0px);
                    }
                `}

            ${Text} + [class*=' icon-'] {
                opacity: 1;
                transition: font-size ease-in-out 200ms,
                    margin ease-in-out 200ms, opacity ease-in-out 200ms 200ms;
            }
        }
    }

    &:focus-visible:not(:disabled) {
        background-color: ${({ theme, variant }) =>
            theme.button[variant].bgColorHover};
        border-color: ${({ theme, variant }) =>
            theme.button[variant].bgColorHover};

        ${Text}, ${Text} + [class*=' icon-'] {
            color: ${({ theme, variant }) =>
                theme.button[variant].contentColorHover};
        }
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover:not(:disabled) {
            ${({ isIconOnly, hasArrow }) =>
                !isIconOnly &&
                hasArrow &&
                css`
                    ${Text} {
                        transform: translateX(0px);
                    }
                `}

            &:hover:not(:disabled) {
                ${({ isIconOnly, hasArrow }) =>
                    !isIconOnly &&
                    hasArrow &&
                    css`
                        ${Text} {
                            transform: translateX(0px);
                        }
                    `}

                ${Text} + [class*=' icon-'] {
                    opacity: 1;
                    transition: font-size ease-in-out 200ms,
                        margin ease-in-out 200ms,
                        opacity ease-in-out 200ms 200ms;
                }
            }
        }

        &:hover:not(:disabled) {
            background-color: ${({ theme, variant }) =>
                theme.button[variant].bgColorHover};
            border-color: ${({ theme, variant }) =>
                theme.button[variant].bgColorHover};

            ${Text}, ${Text} + [class*=' icon-'] {
                color: ${({ theme, variant }) =>
                    theme.button[variant].contentColorHover};
            }
        }
    }

    &:active:not(:disabled) {
        background-color: ${({ theme, variant }) =>
            theme.button[variant].bgColorActive};
        border-color: ${({ theme, variant }) =>
            theme.button[variant].bgColorActive};

        ${Text}, ${Text} + [class*=' icon-'] {
            color: ${({ theme, variant }) =>
                theme.button[variant].contentColorActive};
        }
    }

    &:disabled {
        cursor: initial;
        background-color: transparent;
        border-color: ${({ theme, variant }) =>
            theme.button[variant].borderColorDisabled};

        ${Text} , ${Icon} {
            color: ${({ theme, variant }) =>
                theme.button[variant].contentColorDisabled};
        }
    }
`

interface UnlimitedButtonProps {
    isLoading?: boolean
    background: 'error_bg' | 'secondary'
    width?: number
}

export const UnlimitedButton = styled.button<UnlimitedButtonProps>`
    background-color: ${({ theme, background }) =>
        background === 'error_bg'
            ? theme.colors.error_bg
            : theme.colors.secondary_100};
    height: 48px;
    max-width: ${({ width }) => (width ? `${width}px` : 'initial')};
    width: 100%;
    border-radius: 6px;
    span {
        color: ${({ theme }) => theme.colors.error};
    }

    &:hover {
        background-color: ${({ background }) =>
            background === 'error_bg' ? '#F4CCCE' : '#C7D0FA'};
    }

    &:active {
        background-color: ${({ theme, background }) =>
            background === 'error_bg'
                ? theme.colors.error
                : theme.colors.secondary_500};
        p,
        span {
            color: ${({ theme }) => theme.colors.grayscale_0};
        }
    }

    @media ${respondAbove.xl} {
        border-radius: 8px;
    }
`
